import React, { useEffect, useState } from 'react'

import { DEFAULT_MASKED_PASSWORD } from '../hooks/useEditCamera'
import { UseFormMethods } from 'react-hook-form'

import { Box, FormLabel, HStack } from '@chakra-ui/react'
import { AnimatePresence } from 'framer-motion'

import { FormInputControl, FormInputSelectControl } from '@/components/ui'
import { DeviceCameraEditQuery } from '@/graphql/generated/operations'

import { CameraFormDataTypeI } from '../../types/types'
import {
  getRTSPPath,
  getStreamingProtocolOption,
  getStreamingTypeOption,
  isRTSPFieldRequired,
  shouldHandleRTSPFields,
  streamingProtocols,
  streamingTypes,
  validateIPOrHostname,
  validatePort,
} from '../utils/utils'

interface EditCameraLiveStreamingFieldsIProps {
  cameraData: DeviceCameraEditQuery
  isLoading: boolean
  form: UseFormMethods<CameraFormDataTypeI>
}

export const EditCameraLiveStreamingFields = ({
  cameraData,
  isLoading,
  form,
}: EditCameraLiveStreamingFieldsIProps) => {
  const [isUsernameUpdated, setIsUsernameUpdated] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const {
    errors,
    register,
    control,
    trigger,
    getValues,
    setValue,
    watch,
    clearErrors,
  } = form
  const { rtspPath, rtspIp, rtspPort, streamingProtocol, streamingType } =
    getValues([
      'rtspPath',
      'rtspIp',
      'rtspPort',
      'streamingProtocol',
      'streamingType',
    ])
  watch(['streamingProtocol', 'streamingType'])
  const shouldShowRTSPFields = shouldHandleRTSPFields(streamingType?.value)

  const setLiveStreamType = () => {
    setValue('streamingType', getStreamingTypeOption(cameraData))
  }

  const setRTSPCredentials = () => {
    setValue('rtspPath', cameraData?.device?.camera?.rtspPath)
    setValue('rtspUsername', cameraData?.device?.camera?.rtspUsername)
    setValue('rtspIp', cameraData?.device?.camera?.rtspIp)
    setValue('rtspPort', cameraData?.device?.camera?.rtspPort)
    setValue('streamingProtocol', getStreamingProtocolOption(cameraData))
    if (cameraData?.device?.camera?.rtspUsername) {
      // This is a default value to populate the input, its not actually used.
      setValue('rtspPassword', DEFAULT_MASKED_PASSWORD)
    }
    setIsFirstLoad(false)
  }

  const handleUsernameUpdated = () => {
    if (
      !isUsernameUpdated &&
      cameraData?.device?.camera?.rtspUsername !== getValues('rtspUsername')
    ) {
      // Username has been updated reset password field and mark as required
      register('rtspPassword', { required: true })
      setValue('rtspPassword', '')
      trigger(['rtspPassword'])
      setIsUsernameUpdated(true)
    }
  }

  const updateRequiredFields = () => {
    clearErrors([
      'rtspPath',
      'rtspIp',
      'rtspPort',
      'rtspUsername',
      'rtspPassword',
    ])
    // RSTP is only required for OnPremise
    const isRequired = isRTSPFieldRequired(streamingType)
    register('rtspPath', {
      required: isRequired ? 'RTSP Path is required' : false,
    })
    register('rtspIp', {
      validate: (v) =>
        isRequired ? validateIPOrHostname(v) : validateIPOrHostname(v, false),
    })
    register('rtspPort', {
      validate: (v) => (isRequired ? validatePort(v) : true),
    })
    register('rtspUsername', {
      required: isRequired ? 'RTSP User Name is required' : false,
    })
    register('rtspPassword', {
      required: isRequired ? 'RTSP User Password is required' : false,
    })
  }

  useEffect(() => {
    setLiveStreamType()
  }, [])

  useEffect(() => {
    if (shouldShowRTSPFields && isFirstLoad) {
      setRTSPCredentials()
    }
    updateRequiredFields()
  }, [streamingType?.value])

  return (
    <AnimatePresence key='editCamera'>
      <Box key='streamingType' mb='3'>
        <FormInputSelectControl
          control={control}
          data-testid='devicesPage_editCameraModal_streamingType'
          defaultValue={null}
          errorMessage='Select a streaming type'
          id='streamingType'
          isClearable
          isInvalid={!!errors?.streamingType}
          label='Streaming Type'
          options={streamingTypes}
          placeholder='Select Streaming Type'
          rules={{ required: true }}
        />
      </Box>
      {shouldShowRTSPFields && (
        <>
          <HStack align='stretch' key='streamingProtocol' mb='4' spacing='3'>
            <FormInputSelectControl
              control={control}
              data-testid='devicesPage_editCameraModal_streamingProtocol'
              defaultValue={null}
              errorMessage={!!errors?.streamingProtocol && 'Select Protocol'}
              id='streamingProtocol'
              isClearable
              isDisabled={isLoading}
              isInvalid={!!errors?.streamingProtocol}
              label='Protocol'
              options={streamingProtocols}
              placeholder='Select Protocol'
              tooltipText='Camera protocol'
            />
            <FormInputControl
              data-testid='devicesPage_editCameraModal_rtspPath'
              errorMessage={errors.rtspPath?.message}
              id='rtspPath'
              inputRef={register}
              isInvalid={Boolean(errors.rtspPath)}
              label='RTSP Path'
              onChange={() => trigger(['rtspPath'])}
              placeholder='Enter RTSP Path'
              tooltipText='Path of the RTSP feed'
            />
          </HStack>

          <HStack align='stretch' key='rtspIp' mb='4' spacing='3'>
            <FormInputControl
              data-testid='devicesPage_editCameraModal_rtspIP'
              errorMessage={errors.rtspIp?.message}
              id='rtspIp'
              inputRef={register}
              isInvalid={Boolean(errors.rtspIp)}
              label='RTSP IP/hostname'
              onChange={() => trigger(['rtspIp'])}
              placeholder='Enter RTSP IP'
              tooltipText='IP address or hostname of the RTSP feed'
            />
            <FormInputControl
              data-testid='devicesPage_editCameraModal_rtspPort'
              errorMessage={errors.rtspPort?.message}
              id='rtspPort'
              inputRef={register}
              isInvalid={Boolean(errors.rtspPort)}
              label='RTSP Port'
              onChange={() => trigger(['rtspPort'])}
              placeholder='Enter RTSP Port'
              tooltipText='Port of the RTSP feed'
            />
          </HStack>
          <HStack align='stretch' key='rtspUsername' mb='4' spacing='3'>
            <FormInputControl
              data-testid='devicesPage_editCameraModal_rtspUsername'
              errorMessage={errors.rtspUsername?.message}
              id='rtspUsername'
              inputRef={register}
              isInvalid={Boolean(errors.rtspUsername)}
              label='RTSP User Name'
              onChange={handleUsernameUpdated}
              placeholder='Enter RTSP User Name'
              tooltipText='User name for RTSP credentials'
            />

            <FormInputControl
              errorMessage={errors.rtspPassword?.message}
              id='rtspPassword'
              inputRef={register}
              isInvalid={Boolean(errors.rtspPassword)}
              label='RTSP Password'
              placeholder='Enter RTSP Password'
              tooltipText='Password for RTSP credentials'
              type='password'
            />
          </HStack>

          <Box mb={3}>
            <FormLabel
              color='#2D2E41'
              fontSize='16px'
              margin='0px 0px 8px'
              opacity={0.5}
            >
              RTSP URL
            </FormLabel>
            <Box
              color='#2D2E41'
              fontSize='14px'
              fontWeight='semibold'
              opacity={0.5}
            >
              {getRTSPPath(
                cameraData,
                streamingProtocol?.value,
                rtspPath,
                rtspIp,
                rtspPort
              )}
            </Box>
          </Box>
        </>
      )}
    </AnimatePresence>
  )
}
